/* eslint-disable */
import { Box, Button, TextField, Typography, Avatar, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import { toast } from "react-toastify";
import Constants from "../../config/Constants";
import { Types } from "../../constants/actionTypes";
import styles from "./MasterOrgPages.module.scss";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { TouchFriendlyTooltip } from "../shared";

const AccountSettings = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Get profile from Redux (connected) or from props.props (passed from layout)
  const profileFromRedux = props?.profile;
  const profileFromProps = props?.props?.profile || props?.props?.profile?.data;
  const currentProfile = profileFromRedux || profileFromProps || {};
  const token = currentProfile?.token || props?.props?.profile?.token;
  const userType =
    currentProfile?.user_type || currentProfile?.data?.user_type || 
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  
  // State for fetched profile
  const [fetchedProfile, setFetchedProfile] = useState(null);
  
  // Get initial profile data from Redux or props (will be updated when fetched)
  const initialProfile = profileFromRedux || profileFromProps || {};
  const initialEmail = initialProfile?.email || "";
  const initialOrgName = initialProfile?.organisationDetails?.name || "";
  // Get logo from organisationDetails (same as organizations)
  const initialProfileImage = initialProfile?.organisationDetails?.logo_url || initialProfile?.logo_url || initialProfile?.profile_image || "";

  const [email, setEmail] = useState(initialEmail);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [organisationName, setOrganisationName] = useState(initialOrgName);
  const [logoImageId, setLogoImageId] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(initialProfileImage);
  const [selectedFile, setSelectedFile] = useState(null);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Fetch fresh profile data on component mount
  useEffect(() => {
    if (userType === "master_org" && token) {
      fetchProfileData();
    }
  }, [userType, token]);

  // Store initial values from fetched profile for comparison
  const [initialFetchedValues, setInitialFetchedValues] = useState({
    organisationName: initialOrgName,
    email: initialEmail,
  });

  // Helper: get organisation name from profile
  const getProfileOrgName = (profile) => {
    const orgName = profile?.organisationDetails?.name || profile?.organisationDetails?.organisation_name || profile?.organisation_name || profile?.name || "";
    return typeof orgName === "string" ? orgName : "";
  };

  // Update state when profile data changes (from props or fetched data)
  useEffect(() => {
    const profile = fetchedProfile || profileFromRedux || profileFromProps || {};
    const profileEmail = profile?.email || "";
    const profileOrgName = getProfileOrgName(profile);

    setEmail(profileEmail);
    setOrganisationName(profileOrgName);

    // Update initial fetched values when profile is fetched
    if (fetchedProfile) {
      setInitialFetchedValues({
        organisationName: profileOrgName,
        email: profileEmail,
      });
    }

    // Get logo from organisationDetails (same as organization website)
    const logoUrl = profile?.organisationDetails?.logo_url || profile?.logo_url || profile?.profile_image || "";
    setProfileImageUrl(logoUrl);
    // Preserve existing logo ID if available (from organisationDetails)
    const existingLogoId = profile?.organisationDetails?.id_item_logo || profile?.id_item_logo || null;
    if (existingLogoId) {
      setLogoImageId(existingLogoId);
    }
  }, [profileFromRedux, profileFromProps, fetchedProfile]);

  const fetchProfileData = async () => {
    if (!token) return;

    setLoadingProfile(true);
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };

    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ACCOUNT_GET,
        JSON.stringify({}),
        headers
      );

      if (data?.status && data?.data) {
        const d = data.data;
        const orgName = d.organisation_name ?? d.organisationDetails?.name ?? "";
        const logoUrl = d.profile_image ?? d.logo_url ?? d.organisationDetails?.logo_url ?? "";
        const logoId = d.id_item_logo ?? d.organisationDetails?.id_item_logo ?? null;

        const profileData = {
          organisation_name: orgName,
          organisationDetails: {
            name: orgName,
            logo_url: logoUrl,
            id_item_logo: logoId,
          },
          logo_url: logoUrl,
          profile_image: logoUrl,
          id_item_logo: logoId,
          email: profileFromRedux?.email ?? profileFromProps?.email ?? "",
        };

        setFetchedProfile(profileData);
        setOrganisationName(orgName);
        setProfileImageUrl(logoUrl);
        if (logoId != null) setLogoImageId(logoId);
        setInitialFetchedValues((prev) => ({ ...prev, organisationName: orgName }));
        // Update Redux so sidebar shows organisation name on all master-org pages
        const profileToUpdate = profileFromRedux || profileFromProps || {};
        const updated = {
          ...profileToUpdate,
          organisation_name: orgName,
          organisationDetails: { ...(profileToUpdate?.organisationDetails || {}), name: orgName, logo_url: logoUrl, id_item_logo: logoId },
          profile_image: logoUrl,
          logo_url: logoUrl,
        };
        if (props?.save_user_data) props.save_user_data({ user: updated });
      }
    } catch (error) {
      console.error("Failed to fetch account data", error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      if (file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/jpg") {
        setSelectedFile(file);
        const uploadResponse = await uploadImage(event);
        if (uploadResponse && uploadResponse.status) {
          setLogoImageId(uploadResponse.data.id);
          if (uploadResponse.data.url) {
            setProfileImageUrl(uploadResponse.data.url);
          }
          toast.success("Image uploaded. Click Save changes to update your profile.");
        } else {
          toast.error("Failed to upload image.");
        }
      } else {
        toast.error("Only JPG and PNG files are allowed.");
      }
    }
  };

  const uploadImage = async (e) => {
    const filename = e.target.files[0];
    const formData = new FormData();
    const myHeaders = new Headers();
    myHeaders.append("x-access-token", token);
    formData.append("file", filename, filename.name);
    formData.append("type", "image");

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: formData,
      redirect: "follow",
    };

    try {
      const response = await fetch(`${Constants.BASE_API_URL}user/upload`, requestOptions);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Upload error", error);
      return null;
    }
  };

  const handleSaveProfile = async () => {
    if (!token) return;
    
    const profile = fetchedProfile || profileFromRedux || profileFromProps || {};
    // Get existing logo ID from organisationDetails (same as organizations)
    const existingImageId = profile?.organisationDetails?.id_item_logo || profile?.id_item_logo || null;
    
    // Image changed: new upload (different id) or cleared (null when we had one)
    const imageChanged =
      (logoImageId != null && logoImageId !== existingImageId) ||
      (logoImageId === null && existingImageId != null);
    const orgNameChanged = organisationName.trim() !== (initialFetchedValues.organisationName || "").trim();

    if (!imageChanged && !orgNameChanged) {
      toast.error("No changes to save");
      return;
    }
    if (orgNameChanged && !organisationName.trim()) {
      toast.error("Club name is required");
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    const body = {};
    if (orgNameChanged) {
      body.organisation_name = organisationName.trim();
    }
    if (imageChanged) {
      body.id_item_logo = logoImageId;
    }
    
    setSavingProfile(true);
    props.setIsLoaded(true);
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ACCOUNT_UPDATE,
        JSON.stringify(body),
        headers
      );
      props.setIsLoaded(false);
      setSavingProfile(false);
      
      if (data?.status) {
        toast.success(data.message || "Profile updated");
        const savedName = data?.data?.organisation_name ?? organisationName.trim();
        const savedLogo = data?.data?.profile_image ?? data?.data?.logo_url ?? profileImageUrl;
        setOrganisationName(savedName);
        setProfileImageUrl(savedLogo);
        setInitialFetchedValues({ ...initialFetchedValues, organisationName: savedName });
        // Update Redux so sidebar and alerts use organisation name
        const profileToUpdate = profileFromRedux || profileFromProps || {};
        const existingOrgDetails = profileToUpdate?.organisationDetails || {};
        const updatedProfile = {
          ...profileToUpdate,
          organisationDetails: { ...existingOrgDetails, name: savedName, logo_url: savedLogo },
          profile_image: savedLogo,
          logo_url: savedLogo,
          data: {
            ...(profileToUpdate?.data || {}),
            organisationDetails: { ...existingOrgDetails, name: savedName, logo_url: savedLogo },
            profile_image: savedLogo,
            logo_url: savedLogo,
          },
        };
        if (props?.save_user_data) {
          props.save_user_data({ user: updatedProfile });
        }
        // Refetch so next load shows server state (account/get returns updated organisation_name)
        await fetchProfileData();
      } else {
        toast.error(data?.message || "Failed to update profile");
      }
    } catch (error) {
      props.setIsLoaded(false);
      setSavingProfile(false);
      toast.error("Failed to update profile. Please try again.");
      console.error("Profile update error:", error);
    }
  };

  const handleSaveEmail = async () => {
    if (!token) return;
    
    const currentEmail = fetchedProfile?.email || email;
    const trimmedNewEmail = newEmail.trim();
    
    if (!trimmedNewEmail) {
      toast.error("Please enter a new email address");
      return;
    }
    
    if (trimmedNewEmail === currentEmail) {
      toast.error("Please enter a different email address");
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedNewEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    const body = { email: trimmedNewEmail };
    
    setSavingEmail(true);
    props.setIsLoaded(true);
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ACCOUNT_UPDATE,
        JSON.stringify(body),
        headers
      );
      props.setIsLoaded(false);
      setSavingEmail(false);
      
      if (data?.status) {
        toast.success(data.message || "Email updated");
        setNewEmail("");
        
        // Update Redux state with new email
        const profileToUpdate = profileFromRedux || profileFromProps || {};
        const updatedProfile = {
          ...profileToUpdate,
          email: trimmedNewEmail,
        };
        if (props?.save_user_data) {
          props.save_user_data({ user: updatedProfile });
        }
        
        // Refresh profile data after update
        await fetchProfileData();
      } else {
        toast.error(data?.message || "Failed to update email");
      }
    } catch (error) {
      props.setIsLoaded(false);
      setSavingEmail(false);
      toast.error("Failed to update email. Please try again.");
      console.error("Email update error:", error);
    }
  };

  const handleSavePassword = async () => {
    if (!token) return;
    
    const trimmedPassword = password.trim();
    
    if (!trimmedPassword) {
      toast.error("Please enter a new password");
      return;
    }
    
    // Password validation (minimum 8 characters)
    if (trimmedPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    const body = { password: trimmedPassword };
    
    setSavingPassword(true);
    props.setIsLoaded(true);
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ACCOUNT_UPDATE,
        JSON.stringify(body),
        headers
      );
      props.setIsLoaded(false);
      setSavingPassword(false);
      
      if (data?.status) {
        toast.success(data.message || "Password updated");
        setPassword("");
        // Refresh profile data after update
        await fetchProfileData();
      } else {
        toast.error(data?.message || "Failed to update password");
      }
    } catch (error) {
      props.setIsLoaded(false);
      setSavingPassword(false);
      toast.error("Failed to update password. Please try again.");
      console.error("Password update error:", error);
    }
  };

  if (userType !== "master_org") {
    return (
      <Box className={styles.container}>
        <Typography className={styles.title}>Account Settings</Typography>
        <Typography className={styles.subtitle}>
          Access denied. This page is for club partner accounts.
        </Typography>
      </Box>
    );
  }

  const profileOrgNameChanged =
    organisationName.trim() !== (initialFetchedValues.organisationName || "").trim();
  const existingLogoId =
    fetchedProfile?.organisationDetails?.id_item_logo ?? fetchedProfile?.id_item_logo ?? null;
  const profileImageChanged =
    (logoImageId != null && logoImageId !== existingLogoId) ||
    (logoImageId === null && existingLogoId != null);
  const hasProfileChanges = profileOrgNameChanged || profileImageChanged;

  return (
    <Box className={styles.container}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 700,
              color: isDark ? '#ffffff' : '#1a202c',
              mb: 0.5
            }}
          >
            Account Settings
          </Typography>
          <Typography
            sx={{
              fontSize: '0.9rem',
              color: isDark ? '#cbd5e1' : '#718096'
            }}
          >
            Update your club partner profile, email and password.
          </Typography>
        </Box>
        <TouchFriendlyTooltip
          title={
            <Box sx={{ maxWidth: 320 }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                Tips for account setup
              </Typography>
              <Typography variant="body2">
                • Your display name is used in the app and when you send alerts – keep it up to date.
              </Typography>
              <Typography variant="body2">
                • Upload a clear logo – it will appear on all club and member views.
              </Typography>
              <Typography variant="body2">
                • Keep your contact email up to date so clubs can reach you.
              </Typography>
              <Typography variant="body2">
                • Use a strong password and update it regularly.
              </Typography>
            </Box>
          }
          arrow
        >
          <IconButton
            size="small"
            sx={{ color: isDark ? "#cbd5e1" : "#4a5568" }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </TouchFriendlyTooltip>
      </Box>

      {/* Profile Section – same pattern as Admin: name + photo always editable */}
      <Box className={styles.section} sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: "1.1rem",
            fontWeight: 600,
            color: isDark ? "#ffffff" : "#1a202c",
            mb: 2,
          }}
        >
          Profile
        </Typography>
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: isDark ? "#cbd5e1" : "#64748b",
            mb: 2,
          }}
        >
          Club name is shown in the app and when you send alerts.
        </Typography>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, flexWrap: "wrap" }}>
          <Avatar
            src={profileImageUrl}
            sx={{
              width: 96,
              height: 96,
              bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
              color: isDark ? "#818cf8" : "#1976d2",
              fontSize: "2rem",
              fontWeight: 600,
            }}
          >
            {organisationName ? organisationName.trim().slice(0, 2).toUpperCase() : "O"}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              label="Club name"
              placeholder="e.g. Manchester United"
              value={organisationName}
              onChange={(e) => setOrganisationName(e.target.value)}
              margin="normal"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f7fafc",
                },
              }}
            />
            <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                component="label"
                size="small"
                disabled={savingProfile}
                sx={{ textTransform: "none" }}
              >
                Upload photo
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleFileChange}
                />
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const profile = fetchedProfile || profileFromRedux || profileFromProps || {};
                  setLogoImageId(null);
                  setProfileImageUrl("");
                  setSelectedFile(null);
                }}
                disabled={savingProfile || !profileImageUrl}
                sx={{
                  textTransform: "none",
                  color: isDark ? "#f87171" : "#dc2626",
                  borderColor: isDark ? "#f87171" : "#dc2626",
                }}
              >
                Clear photo
              </Button>
            </Box>
          </Box>
        </Box>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={savingProfile || !hasProfileChanges}
            startIcon={savingProfile ? null : <SaveIcon />}
            sx={{
              textTransform: "none",
              backgroundColor: isDark ? "#6366f1" : "#1976d2",
              "&:hover": { backgroundColor: isDark ? "#4f46e5" : "#1565c0" },
            }}
          >
            {savingProfile ? "Saving…" : "Save changes"}
          </Button>
        </Box>
      </Box>

      {/* Email Section */}
      <Box className={styles.section} sx={{ mb: 3 }}>
        <Typography 
          sx={{ 
            fontSize: '1.1rem', 
            fontWeight: 600,
            color: isDark ? '#ffffff' : '#1a202c',
            mb: 2
          }}
        >
          Email Address
        </Typography>
        <Typography 
          sx={{ 
            fontSize: '0.875rem',
            color: isDark ? '#cbd5e1' : '#64748b',
            mb: 2
          }}
        >
          Current: {email}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
          <TextField
            label="New Email"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            fullWidth
            type="email"
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            onClick={handleSaveEmail}
            disabled={savingEmail || !newEmail}
            startIcon={<SaveIcon />}
            fullWidth={isMobile}
            sx={{
              textTransform: 'none',
              backgroundColor: isDark ? '#6366f1' : '#1976d2',
              '&:hover': {
                backgroundColor: isDark ? '#4f46e5' : '#1565c0',
              },
              mt: { xs: 0, sm: 0.5 },
              minHeight: { xs: 44, sm: 'auto' },
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {savingEmail ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      {/* Password Section */}
      <Box className={styles.section}>
        <Typography 
          sx={{ 
            fontSize: '1.1rem', 
            fontWeight: 600,
            color: isDark ? '#ffffff' : '#1a202c',
            mb: 2
          }}
        >
          Password
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
          <TextField
            label="New Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            fullWidth
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            onClick={handleSavePassword}
            disabled={savingPassword || !password}
            startIcon={<SaveIcon />}
            fullWidth={isMobile}
            sx={{
              textTransform: 'none',
              backgroundColor: isDark ? '#6366f1' : '#1976d2',
              '&:hover': {
                backgroundColor: isDark ? '#4f46e5' : '#1565c0',
              },
              mt: { xs: 0, sm: 0.5 },
              minHeight: { xs: 44, sm: 'auto' },
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {savingPassword ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  profile: state.user.profile,
});

const mapDispatchToProps = (dispatch) => ({
  save_user_data: (data) => dispatch({ type: Types.LOGIN, payload: data }),
});

export default connect(mapStateToProps, mapDispatchToProps)(AccountSettings);
